import { Button, Modal } from '@praktio/common-ui';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { copyTeam, updateTeamSettings } from '../../../../ducks/teams';
import { buildTeamHierarchy, notifyError, notifySuccess } from '../../../../utils/functions';
import { highlightMatch } from '../../../../utils';
import { IMoveTeamModalProps, SearchResult, TeamNode } from './MoveTeamModal.types';
import { findPathToTeam, mapTeamToNode, searchAllTeams } from './MoveTeamModal.utils';

import './MoveTeamModal.scss';

const MoveTeamModal = ({ team, showModal, teams, onHide }: IMoveTeamModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const teamHierarchy = useMemo(() => buildTeamHierarchy({ teams, sorted: true }), [teams]);

  const teamTree = useMemo(
    () => [
      {
        id: 'root',
        name: 'All Teams',
        children: teamHierarchy.map(mapTeamToNode),
      },
    ],
    [teamHierarchy],
  );

  const getInitialPath = useCallback((): TeamNode[] => {
    if (!team?.parent) {
      return teamTree;
    }

    const pathToParent = findPathToTeam(teamTree, team.parent);

    return pathToParent || teamTree;
  }, [team?.parent, teamTree]);

  const [movePath, setMovePath] = useState<TeamNode[]>(getInitialPath);
  const [inputValue, setInputValue] = useState(''); // Immediate input display
  const [searchValue, setSearchValue] = useState(''); // Debounced search value
  const [showDropdown, setShowDropdown] = useState(false);

  const searchContainerRef = useRef<HTMLDivElement>(null);

  const isAtRoot = movePath[movePath.length - 1].id === teamTree[0].id;

  const destinationParent = isAtRoot
    ? { id: null, name: 'Top Level' }
    : movePath[movePath.length - 1];

  const availableChildren = (movePath[movePath.length - 1].children || []).filter(
    (child) => child.id !== team?._id,
  );

  const debouncedSetSearchValue = useRef(
    debounce((value: string) => {
      setSearchValue(value);
    }, 150),
  ).current;

  const searchResults = useMemo(() => {
    if (!searchValue) return [];
    return searchAllTeams(teamTree, searchValue, [], team?._id);
  }, [searchValue, teamTree, team?._id]);

  const clearSearchValue = () => {
    setInputValue('');
    setSearchValue('');
    setShowDropdown(false);
  };

  // Render Conditions

  const hasSearchResults = searchResults.length > 0;
  const showResultsDropdown = showDropdown && hasSearchResults;
  const showNoResultsMessage = showDropdown && searchValue && !hasSearchResults;
  const hasChildTeams = availableChildren.length > 0;

  const currentParentId = team?.parent || null;
  const isSameLocation = destinationParent?.id === currentParentId;

  // Handlers

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSetSearchValue(value);
    setShowDropdown(!!value);
  };

  // Navigate to a team from search results (go INTO that team's level)
  const handleSearchResultClick = (result: SearchResult) => {
    setMovePath(result.path);
    clearSearchValue();
  };

  // Navigate to a breadcrumb segment in search results
  const handleSearchBreadcrumbClick = (path: TeamNode[], index: number) => {
    setMovePath(path.slice(0, index + 1));
    clearSearchValue();
  };

  const handleBack = () => {
    if (movePath.length <= 1) return;
    clearSearchValue();
    setMovePath(movePath.slice(0, -1));
  };

  const handleBreadcrumbClick = (index: number) => {
    clearSearchValue();
    setMovePath(movePath.slice(0, index + 1));
  };

  const handleTeamClick = (team: TeamNode) => {
    clearSearchValue();
    setMovePath([...movePath, team]);
  };

  const handleMove = async () => {
    if (!team) {
      console.error('No team selected for moving');
      return;
    }

    try {
      await dispatch(
        updateTeamSettings(team?._id, {
          name: team.name,
          useWhiteFilter: !!team.useWhiteFilter,
          isUnlocked: !!team.isUnlocked,
          enableTimeRelease: !!team.enableTimeRelease,
          ignoreExpiration: !!team.ignoreExpiration,
          expirationOverride: team.expirationOverride,
          logo: team.logo,
          signupCodes: team.signupCodes,
          isMarmonTeam: false,
          parent: destinationParent?.id,
        }),
      );
      notifySuccess('Team moved');
    } catch (e) {
      notifyError(e, dispatch);
    } finally {
      onHide();
    }
  };

  const handleCopy = async () => {
    if (!team) {
      console.error('No team selected for copying');
      return;
    }

    try {
      await dispatch(copyTeam(team._id, destinationParent?.id, navigate));
    } catch (e) {
      notifyError(e, dispatch);
    } finally {
      onHide();
    }
  };

  // Effects

  useEffect(() => {
    setMovePath(getInitialPath());
  }, [team?._id, getInitialPath]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Modal
      className="MoveTeamModal"
      title={`Move or Copy "${team?.name}"${
        destinationParent ? ` to "${destinationParent.name}"` : ''
      }`}
      description="Select a destination"
      show={showModal}
      saveText="Copy"
      onSave={handleCopy}
      onHide={onHide}
      footerAdditions={
        <div className="MoveTeamModal__FooterButtons">
          <Button className="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            btnStyle="primary"
            disabled={isSameLocation}
            onClick={handleMove}
            tooltip={
              isSameLocation
                ? 'The selected destination is the same as the current location of this team.'
                : undefined
            }
          >
            Move
          </Button>
        </div>
      }
    >
      <div className="MoveTeamModal__Container">
        {/* Search Input with Dropdown */}
        <div className="MoveTeamModal__SearchContainer" ref={searchContainerRef}>
          <Form.Control
            className="MoveTeamModal__Search"
            placeholder="Search teams..."
            value={inputValue}
            onChange={handleSearchChange}
            onFocus={() => inputValue && setShowDropdown(true)}
          />

          {showResultsDropdown ? (
            <div
              className="MoveTeamModal__SearchDropdown"
              role="listbox"
              aria-label="Search results"
            >
              {searchResults.map((result) => {
                const hasParentPath = result.path.length > 1;
                const parentSegments = result.path.slice(0, -1);

                return (
                  <div
                    key={result.team.id}
                    className="MoveTeamModal__SearchResult"
                    role="option"
                    aria-selected={false}
                    tabIndex={0}
                    onClick={() => handleSearchResultClick(result)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearchResultClick(result);
                      }
                    }}
                  >
                    <span className="MoveTeamModal__SearchResultName">
                      {highlightMatch(result.team.name, searchValue)}
                    </span>

                    {hasParentPath ? (
                      <div className="MoveTeamModal__SearchResultPath">
                        {parentSegments.map((segment, idx) => {
                          const isLastSegment = idx === parentSegments.length - 1;

                          return (
                            <span key={segment.id}>
                              <button
                                type="button"
                                tabIndex={-1}
                                className="MoveTeamModal__SearchResultPathBtn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSearchBreadcrumbClick(result.path, idx);
                                }}
                              >
                                {segment.name}
                              </button>

                              {!isLastSegment && (
                                <span className="MoveTeamModal__SearchResultPathSep">{' > '}</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}

          {showNoResultsMessage ? (
            <div className="MoveTeamModal__SearchDropdown">
              <div className="MoveTeamModal__SearchNoResults">No teams found</div>
            </div>
          ) : null}
        </div>

        {/* Breadcrumb Row */}
        <div className="MoveTeamModal__BreadcrumbRow">
          {!isAtRoot && (
            <button
              className="MoveTeamModal__BackBtn"
              type="button"
              aria-label="Go back"
              onClick={handleBack}
            >
              <span className="material-icons">arrow_back</span>
            </button>
          )}

          <div className="MoveTeamModal__Breadcrumbs">
            {movePath.map((team, idx) => {
              const isClickable = idx < movePath.length - 1;

              return (
                <span key={team.id} style={{ display: 'flex', alignItems: 'center' }}>
                  {isClickable ? (
                    <button
                      className="MoveTeamModal__BreadcrumbBtn"
                      aria-label={`Go to ${team.name}`}
                      type="button"
                      onClick={() => handleBreadcrumbClick(idx)}
                    >
                      {team.name}
                    </button>
                  ) : (
                    <span className="MoveTeamModal__BreadcrumbCurrent">{team.name}</span>
                  )}

                  {isClickable ? <span className="MoveTeamModal__BreadcrumbSep">{'>'}</span> : null}
                </span>
              );
            })}
          </div>
        </div>

        {/* Child Teams List */}
        {hasChildTeams ? (
          <div className="MoveTeamModal__ChildrenList">
            {availableChildren.map((child) => (
              <div
                key={child.id}
                className="MoveTeamModal__Child"
                onClick={() => handleTeamClick(child)}
              >
                <span>{child.name}</span>
                <span className="material-icons">chevron_right</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default MoveTeamModal;
